import { isBrandingDesktopAdUnit, isMailAdUnit, AdUnit, Collapse } from 'mk/ox/hbAdUnits';
import { PREFIX_AD_SLOT_INTERSTITIAL } from 'mk2/containers/AdSlotInterstitial/AdSlotInterstitial';
import { cacheLast } from 'mk2/helpers/cache';
import { getLogger } from 'mk2/logger';

const logger = getLogger('AdSlot');

const maxHeightWithAdCache = cacheLast(true);

/**
 * Returns the maximum height of an ad unit based on the screen size and ad unit configuration.
 * If the ad unit is configured to collapse, it returns null.
 */
function getMaxHeightWithAdUncached(adUnit: AdUnit, isMobile: boolean): number | null {
    const shouldDisplayPlaceholder =
        adUnit.collapse === Collapse.NEVER || adUnit.collapse === Collapse.AFTER_FETCH;

    if (!shouldDisplayPlaceholder) return null;

    let maxScreenHeight: number | null = null;

    const screenWidth = typeof window !== 'undefined'
        ? document.body.clientWidth
        : isMobile
            ? 599
            : 980;

    for (const ssc of adUnit.sizeConfig) {
        if (ssc.minScreenWidth <= screenWidth) {
            for (const [width, height] of ssc.bannerSizes) {
                const isHeightBetter = maxScreenHeight === null || height > maxScreenHeight;
                const isNotBranding = !(height === 1400 && isBrandingDesktopAdUnit(adUnit));
                const isNotMail = !isMailAdUnit(adUnit);

                if (isHeightBetter && isNotBranding && isNotMail) {
                    maxScreenHeight = height;
                }
            }
        }
    }

    const allowedHeights = new Set([50, 60, 82, 90, 100, 112, 117, 120, 122, 125, 180, 200, 210, 250, 280, 300, 600]);
    return allowedHeights.has(maxScreenHeight!) ? maxScreenHeight : null;
}


export function getMaxHeightWithAd(adUnit: AdUnit, isMobile: boolean) {
    return maxHeightWithAdCache(() => getMaxHeightWithAdUncached(adUnit, isMobile), adUnit, typeof window);
}

export const isDev = () => process.env.NODE_ENV === 'development';

let _lastSearch = '';
let _adsDbg = false;

export const dbg = (...args: any[]): void => {
    let currentSearch = '';
    try {
        if (typeof window !== 'undefined' && window.location?.search != null) {
            currentSearch = window.location.search;
        }
    } catch { /* pass */ }

    // ak sa zmenil search, prepočítaj adsdbg
    if (currentSearch !== _lastSearch) {
        _lastSearch = currentSearch;
        try {
            const params = new URLSearchParams(currentSearch);
            _adsDbg = params.get('adsdbg') === '1';
        } catch {
            _adsDbg = false;
        }
    }

    if (!isDev() && !_adsDbg) return;

    if (typeof logger?.debug === 'function') {
        const ts = new Date().toISOString();
        const tsm = Math.round((performance?.now?.() ?? 0));
        const last = args[args.length - 1];
        if (last && typeof last === 'object' && !Array.isArray(last)) {
            // posledný arg je objekt → doplň timestamp polia
            logger.debug(...args.slice(0, -1), { ...last, ts, tsm });
        } else {
            // inak pridaj samostatný objekt
            logger.debug(...args, { ts, tsm });
        }
    }
};

export const withGpt = (fn: () => void) => googletag?.cmd?.push(fn);

export const isInterstitialId = (id: string) => id.startsWith(PREFIX_AD_SLOT_INTERSTITIAL);

export function setPageDetailsToTargeting() {
  const raw = (window?.location?.pathname || '/').replace(/\/+$/, '') || '/';
  const firstSeg = '/' + (raw.split('/').filter(Boolean)[0] || '');
  return { page: firstSeg, page_path: raw };
}
